/**
 * ArrTooltip Component
 *
 * A wrapper around MUI's Tooltip that supports two trigger modes:
 * - "hover" → standard tooltip behavior (default)
 * - "click" → tooltip opens/closes on click, with outside click handling
 *
 * Features:
 * - Can render string content (wrapped in Typography) or custom ReactNode
 * - Optional truncation handling:
 *   - `isTruncated`: whether text is visually truncated
 *   - `showOnTruncate`: if true, only show tooltip when truncated
 * - Configurable offset, placement, and arrow
 * - Renders either a custom child element or a default Info icon button
 *
 * Use cases:
 * - Show extra info on hover/click for icons, text, or UI elements
 * - Ensure tooltips only appear when text is truncated
 */

import React, {
  useState,
  useRef,
  useEffect,
  ReactNode,
  useLayoutEffect,
} from 'react';
import {Tooltip, Box, TooltipProps, Typography} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import './ArrTooltip.scss';
import ArrIconButton from '../arr-icon-button/ArrIconButton';
import {useAnalytics} from '../../../hooks/useAnalytics';
import {
  GAHelpAlertVariant,
  trackHelpOrAlertView,
} from '../../../utils/analyticsUtils';
import {GA_SUBPAGE_TYPE} from '../../../constants/analyticsConstants';

type TriggerType = 'hover' | 'click';
type SizeType = 'small' | 'medium' | 'large';

interface ArrTooltipProps {
  /** Tooltip content (string or custom ReactNode) */
  content: string | ReactNode;
  /** How tooltip is triggered (hover or click). Default = hover */
  trigger?: TriggerType;
  /** Placement of tooltip relative to target (top, bottom, etc.) */
  placement?: TooltipProps['placement'];
  /** Whether the child text is truncated (used with showOnTruncate) */
  isTruncated?: boolean;
  /** Only show tooltip if text is truncated */
  showOnTruncate?: boolean;
  /** Popper offset distance from the anchor */
  offset?: number;
  /** Size of default icon button (small, medium, large) */
  size?: SizeType;
  /** Custom child element to wrap with tooltip */
  children?: ReactNode;
  /** Additional className for custom styling */
  className?: string;
  contentClassName?: string;
  hideInfoIconOnEmptyChildren?: boolean;
  defaultOpen?: boolean;
  tooltipKey?: string;
  setAnchorElementWidth?: (width: number) => void;
  flipEnabled?: boolean;
  tabIndex?: number;
  focusableWrapper?: boolean;
  labelId?: string;
  gaConfig?: {
    variant?: GAHelpAlertVariant;
    details?: string;
  };
}

const ArrTooltip: React.FC<ArrTooltipProps> = ({
  content,
  trigger = 'hover',
  placement = 'top',
  isTruncated = true,
  showOnTruncate = false,
  offset = 0,
  size = 'small',
  children,
  contentClassName,
  className,
  hideInfoIconOnEmptyChildren = false,
  defaultOpen,
  tooltipKey = '',
  setAnchorElementWidth,
  flipEnabled = true,
  tabIndex,
  focusableWrapper = false,
  labelId,
  gaConfig,
  ...rest
}) => {
  /** Tooltip open/close state (only controlled manually for trigger="click") */
  const [open, setOpen] = useState(false);
  const {trackGAEvent} = useAnalytics();

  /** Ref to anchor element (the element tooltip is attached to) */
  const anchorRef = useRef<HTMLDivElement | null>(null);

  /** Ref to tooltip popper element */
  const tooltipRef = useRef<HTMLDivElement | null>(null);

  const tooltipId = `tooltip-${tooltipKey || 'default'}`;

  // Option to set the Width of Label area in dropdown - Use for for calculating dynamic labels based on space available
  useLayoutEffect(() => {
    if (!anchorRef.current) return;
    const el = anchorRef.current;
    setAnchorElementWidth?.(el.clientWidth);
  }, []);

  /**
   * Effect: Handles closing tooltip when clicking outside.
   * Runs only when trigger="click" and tooltip is currently open.
   */
  useEffect(() => {
    if (trigger !== 'click' || !open) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        anchorRef.current?.contains(target) || // click inside anchor
        tooltipRef.current?.contains(target) // click inside tooltip
      ) {
        return; // do nothing
      }
      setOpen(false); // close tooltip
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open, trigger]);

  /** Toggle tooltip when clicked (used only for trigger="click") */
  const handleClick = (event?: React.MouseEvent) => {
    if (trigger === 'click') {
      // Stop click events from propagating to parent elements
      event?.stopPropagation();
      if (!open && gaConfig) {
        trackHelpOrAlertView(trackGAEvent, {
          variant: gaConfig.variant,
          subpageType: GA_SUBPAGE_TYPE.TOOLTIP,
          details: gaConfig.details,
        });
      }
      setOpen((prev) => !prev);
    }
  };

  /** Close tooltip programmatically (used only for trigger="click") */
  const handleClose = () => {
    if (trigger === 'click') setOpen(false);
  };

  /** Render tooltip content:
   * - If string → wrap in Typography
   * - If ReactNode → render directly
   */
  const TooltipContent =
    typeof content === 'string' ? (
      <Typography
        className={`tooltip-content ${contentClassName}`}
        data-testid={`arr-tooltip-content`}
      >
        {content}
      </Typography>
    ) : (
      content
    );

  /**
   * Controls whether tooltip should be shown:
   * - If showOnTruncate=false → always show
   * - If showOnTruncate=true → only show if isTruncated=true
   */
  const shouldShowTooltip = !showOnTruncate || (showOnTruncate && isTruncated);

  return (
    <Tooltip
      /** For trigger="click", control open state manually */
      key={tooltipKey}
      open={trigger === 'click' ? open : defaultOpen}
      onClose={handleClose}
      disableFocusListener={trigger === 'click'}
      disableHoverListener={trigger === 'click'}
      disableTouchListener={trigger === 'click'}
      /** Tooltip content or empty if not allowed */
      id={tooltipId}
      title={shouldShowTooltip ? TooltipContent : undefined}
      className={className}
      placement={placement}
      arrow
      slotProps={{
        popper: {
          ref: tooltipRef,
          modifiers: [
            {
              /** Add offset between anchor and tooltip */
              name: 'offset',
              options: {offset: [0, offset + 6]},
            },
            {
              /** Prevent tooltip from overflowing viewport */
              name: 'preventOverflow',
              options: {boundary: 'viewport'},
            },
            {
              /** Allow tooltip to flip placement when needed */
              name: 'flip',
              enabled: flipEnabled,
              options: {
                fallbackPlacements: ['top', 'bottom', 'left', 'right'],
              },
            },
          ],
        },
        tooltip: {
          /** Inline styles for tooltip box */
          sx: {
            margin: '0rem !important',
            padding: '0rem',
            minWidth: '3rem',
            maxWidth: {
              xs: '12.5rem',
              sm: '15rem',
              md: '17.5rem',
            },
          },
        },
      }}
      disableInteractive={false}
      {...rest}
    >
      <Box
        component="span"
        ref={anchorRef}
        onClick={trigger === 'click' ? (e) => handleClick(e) : undefined}
        display="inline"
        data-testid={
          labelId ??
          (typeof content === 'string' ? content : undefined) ??
          'tool-tip'
        }
        role={focusableWrapper ? 'presentation' : 'button'}
        tabIndex={focusableWrapper ? -1 : (tabIndex ?? 0)}
        aria-expanded={trigger === 'click' ? open : undefined}
        aria-describedby={open ? tooltipId : undefined}
        onKeyDown={
          focusableWrapper
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleClick();
                }
              }
            : undefined
        }
      >
        {/** If children are passed → render them, else render default icon button */}
        {children || hideInfoIconOnEmptyChildren || (
          <ArrIconButton
            icon={<InfoOutlinedIcon />}
            onClick={() => {}}
            ariaLabel="info-icon"
            shape="default"
            size={size}
            testId="tool-tip-info-btn"
          />
        )}
      </Box>
    </Tooltip>
  );
};

export default ArrTooltip;
